import type { ApiClient } from '@/helpers/apiClient'
import {
  webhookDeliveryDetailDtoSchema,
  webhookDeliveryListDtoSchema,
  webhookTestResultDtoSchema,
  type WebhookDeliveryDetailDto,
  type WebhookDeliveryListDto,
  type WebhookTestResultDto,
} from '@lib/dto/webhook-delivery.dto'
import type { TenantMode } from '@lib/enums/tenant-mode.enum'
import type { EnvelopeWebhookEventName } from '@lib/enums/webhook-event.enum'
import type { WebhookDeliveryQuery } from '@lib/input/webhook-delivery.input'
import type { Uuid } from '@lib/validators/params'
import type { IWebhookLogService } from '@/types/WebhookLogService.types'

function base(tenantId: Uuid, mode: TenantMode): string {
  return `/dashboard/tenants/${encodeURIComponent(tenantId)}/modes/${mode}/webhooks`
}

/** The webhook log, bound to a dashboard session's client (the tenant's, or staff view-as). */
export class WebhookLogService implements IWebhookLogService {
  constructor(private readonly client: ApiClient) {}

  list(tenantId: Uuid, mode: TenantMode, query: WebhookDeliveryQuery): Promise<WebhookDeliveryListDto> {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value) params.set(key, value)
    }
    const search = params.toString()
    return this.client.get(`${base(tenantId, mode)}${search ? `?${search}` : ''}`, webhookDeliveryListDtoSchema)
  }

  detail(tenantId: Uuid, mode: TenantMode, deliveryId: Uuid): Promise<WebhookDeliveryDetailDto> {
    return this.client.get(`${base(tenantId, mode)}/${encodeURIComponent(deliveryId)}`, webhookDeliveryDetailDtoSchema)
  }

  replay(tenantId: Uuid, mode: TenantMode, deliveryId: Uuid): Promise<WebhookDeliveryDetailDto> {
    return this.client.post(
      `${base(tenantId, mode)}/${encodeURIComponent(deliveryId)}/replay`,
      {},
      webhookDeliveryDetailDtoSchema,
    )
  }

  sendTestEvent(tenantId: Uuid, event: EnvelopeWebhookEventName): Promise<WebhookTestResultDto> {
    return this.client.post(`${base(tenantId, 'test')}/test-event`, { event }, webhookTestResultDtoSchema)
  }
}
